import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import AdminNav from "@/components/AdminNav";
import { api } from "@/lib/api";
import { authHeaders, getToken } from "@/lib/auth";
import { refreshCms } from "@/lib/cms";

const defaultCms = {
  hero: { eyebrow: "", title_top: "", title_bottom: "", description: "", stats: [] },
  about: { eyebrow: "", title: "", paragraph1: "", paragraph2: "" },
  why_us: { eyebrow: "", title: "", cards: [] },
  testimonials: { eyebrow: "", title: "", items: [] },
  contact: { eyebrow: "", title: "", description: "", email: "", phone: "", address: "" },
  footer: { description: "" },
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const [cms, setCms] = useState(defaultCms);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/cms");
        setCms({ ...defaultCms, ...(data || {}) });
      } catch {
        toast.error("Failed to load settings.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const update = (section, patch) =>
    setCms((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/cms", cms, { headers: authHeaders() });
      await refreshCms();
      toast.success("Settings saved. Website updated.");
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  // --- helpers for arrays ---
  const updateArrayItem = (section, key, idx, patch) => {
    setCms((prev) => {
      const arr = [...(prev[section]?.[key] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, [section]: { ...prev[section], [key]: arr } };
    });
  };
  const addArrayItem = (section, key, item) => {
    setCms((prev) => {
      const arr = [...(prev[section]?.[key] || []), item];
      return { ...prev, [section]: { ...prev[section], [key]: arr } };
    });
  };
  const removeArrayItem = (section, key, idx) => {
    setCms((prev) => {
      const arr = [...(prev[section]?.[key] || [])];
      arr.splice(idx, 1);
      return { ...prev, [section]: { ...prev[section], [key]: arr } };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <AdminNav />
        <div className="p-20 text-center text-slate-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page" className="min-h-screen bg-[#F8FAFC] pb-32">
      <AdminNav />
      <div className="px-6 md:px-12 lg:px-24 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7]">
              Manage Content
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 mt-3">
              Site Settings
            </h1>
            <p className="text-slate-600 mt-3 text-sm max-w-2xl">
              Edit any text on your website. Click <strong>Save Changes</strong> at the bottom
              to publish — changes go live immediately.
            </p>
          </div>
          <Button
            data-testid="settings-save-top"
            onClick={save}
            disabled={saving}
            className="rounded-full h-11 bg-[#0A2540] hover:bg-[#0F172A] text-white px-6"
          >
            <Save size={16} strokeWidth={1.5} className="mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <Accordion type="multiple" defaultValue={["contact"]} className="space-y-3">
          {/* HERO */}
          <SectionItem value="hero" title="Hero Section" subtitle="The first thing visitors see on the homepage">
            <Row label="Small tag (above heading)">
              <Input value={cms.hero?.eyebrow || ""} onChange={(e) => update("hero", { eyebrow: e.target.value })} />
            </Row>
            <Row label="Heading – Line 1">
              <Input value={cms.hero?.title_top || ""} onChange={(e) => update("hero", { title_top: e.target.value })} />
            </Row>
            <Row label="Heading – Line 2 (highlighted)">
              <Input value={cms.hero?.title_bottom || ""} onChange={(e) => update("hero", { title_bottom: e.target.value })} />
            </Row>
            <Row label="Description (long text)">
              <Textarea
                value={cms.hero?.description || ""}
                onChange={(e) => update("hero", { description: e.target.value })}
                className="min-h-[100px]"
              />
            </Row>
            <Row label="Hero Stats (3 small numbers below heading)">
              <ArrayEditor
                items={cms.hero?.stats || []}
                fields={[{ key: "k", label: "Label" }, { key: "v", label: "Value" }]}
                onChange={(idx, patch) => updateArrayItem("hero", "stats", idx, patch)}
                onAdd={() => addArrayItem("hero", "stats", { k: "", v: "" })}
                onRemove={(idx) => removeArrayItem("hero", "stats", idx)}
                addLabel="Add stat"
              />
            </Row>
          </SectionItem>

          {/* ABOUT */}
          <SectionItem value="about" title="About Section">
            <Row label="Small tag"><Input value={cms.about?.eyebrow || ""} onChange={(e) => update("about", { eyebrow: e.target.value })} /></Row>
            <Row label="Heading"><Input value={cms.about?.title || ""} onChange={(e) => update("about", { title: e.target.value })} /></Row>
            <Row label="Paragraph 1">
              <Textarea value={cms.about?.paragraph1 || ""} onChange={(e) => update("about", { paragraph1: e.target.value })} className="min-h-[100px]" />
            </Row>
            <Row label="Paragraph 2">
              <Textarea value={cms.about?.paragraph2 || ""} onChange={(e) => update("about", { paragraph2: e.target.value })} className="min-h-[100px]" />
            </Row>
          </SectionItem>

          {/* WHY US */}
          <SectionItem value="why_us" title="Why Us Section">
            <Row label="Small tag"><Input value={cms.why_us?.eyebrow || ""} onChange={(e) => update("why_us", { eyebrow: e.target.value })} /></Row>
            <Row label="Heading"><Input value={cms.why_us?.title || ""} onChange={(e) => update("why_us", { title: e.target.value })} /></Row>
            <Row label="Cards (3 columns on the homepage)">
              <ArrayEditor
                items={cms.why_us?.cards || []}
                fields={[{ key: "title", label: "Card Title" }, { key: "text", label: "Card Text", textarea: true }]}
                onChange={(idx, patch) => updateArrayItem("why_us", "cards", idx, patch)}
                onAdd={() => addArrayItem("why_us", "cards", { title: "", text: "" })}
                onRemove={(idx) => removeArrayItem("why_us", "cards", idx)}
                addLabel="Add card"
              />
            </Row>
          </SectionItem>

          {/* TESTIMONIALS */}
          <SectionItem value="testimonials" title="Testimonials / Voices Section">
            <Row label="Small tag"><Input value={cms.testimonials?.eyebrow || ""} onChange={(e) => update("testimonials", { eyebrow: e.target.value })} /></Row>
            <Row label="Heading"><Input value={cms.testimonials?.title || ""} onChange={(e) => update("testimonials", { title: e.target.value })} /></Row>
            <Row label="Testimonials">
              <ArrayEditor
                items={cms.testimonials?.items || []}
                fields={[
                  { key: "name", label: "Name" },
                  { key: "title", label: "Title / Role" },
                  { key: "quote", label: "Quote", textarea: true },
                ]}
                onChange={(idx, patch) => updateArrayItem("testimonials", "items", idx, patch)}
                onAdd={() => addArrayItem("testimonials", "items", { name: "", title: "", quote: "" })}
                onRemove={(idx) => removeArrayItem("testimonials", "items", idx)}
                addLabel="Add testimonial"
              />
            </Row>
          </SectionItem>

          {/* CONTACT */}
          <SectionItem value="contact" title="Contact Section + Footer Contact Info" subtitle="Appears on the contact section and in the footer">
            <Row label="Small tag"><Input value={cms.contact?.eyebrow || ""} onChange={(e) => update("contact", { eyebrow: e.target.value })} /></Row>
            <Row label="Heading"><Input value={cms.contact?.title || ""} onChange={(e) => update("contact", { title: e.target.value })} /></Row>
            <Row label="Description (short)"><Textarea value={cms.contact?.description || ""} onChange={(e) => update("contact", { description: e.target.value })} className="min-h-[80px]" /></Row>
            <Row label="Email"><Input value={cms.contact?.email || ""} onChange={(e) => update("contact", { email: e.target.value })} /></Row>
            <Row label="Phone"><Input value={cms.contact?.phone || ""} onChange={(e) => update("contact", { phone: e.target.value })} /></Row>
            <Row label="HQ Address"><Textarea value={cms.contact?.address || ""} onChange={(e) => update("contact", { address: e.target.value })} className="min-h-[70px]" /></Row>
          </SectionItem>

          {/* FOOTER */}
          <SectionItem value="footer" title="Footer">
            <Row label="Footer description (under the logo)">
              <Textarea value={cms.footer?.description || ""} onChange={(e) => update("footer", { description: e.target.value })} className="min-h-[100px]" />
            </Row>
          </SectionItem>
        </Accordion>

        <div className="sticky bottom-4 mt-10 flex justify-end">
          <Button
            data-testid="settings-save-bottom"
            onClick={save}
            disabled={saving}
            className="rounded-full h-12 bg-[#0A2540] hover:bg-[#0F172A] text-white px-7 shadow-[0_15px_40px_rgba(10,37,64,0.25)]"
          >
            <Save size={16} strokeWidth={1.5} className="mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const SectionItem = ({ value, title, subtitle, children }) => (
  <AccordionItem value={value} className="rounded-2xl border border-slate-100 bg-white px-5 data-[state=open]:shadow-[0_8px_30px_rgba(10,37,64,0.05)]">
    <AccordionTrigger className="hover:no-underline py-5">
      <div className="text-left">
        <p className="font-heading text-lg font-medium text-slate-900">{title}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </AccordionTrigger>
    <AccordionContent className="pb-6 space-y-5">{children}</AccordionContent>
  </AccordionItem>
);

const Row = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-2">{label}</label>
    {children}
  </div>
);

const ArrayEditor = ({ items, fields, onChange, onAdd, onRemove, addLabel }) => (
  <div className="space-y-3">
    {items.map((it, idx) => (
      <div key={idx} className="rounded-xl bg-slate-50 border border-slate-100 p-4 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold block mb-1.5">
                {f.label}
              </label>
              {f.textarea ? (
                <Textarea
                  value={it[f.key] || ""}
                  onChange={(e) => onChange(idx, { [f.key]: e.target.value })}
                  className="min-h-[70px] rounded-lg bg-white"
                />
              ) : (
                <Input
                  value={it[f.key] || ""}
                  onChange={(e) => onChange(idx, { [f.key]: e.target.value })}
                  className="h-10 rounded-lg bg-white"
                />
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onRemove(idx)}
          className="absolute top-3 right-3 w-7 h-7 rounded-full border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center"
          aria-label="Remove"
        >
          <Trash2 size={12} strokeWidth={1.5} />
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={onAdd}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0A2540] hover:text-[#0F172A]"
    >
      <Plus size={14} strokeWidth={1.5} /> {addLabel}
    </button>
  </div>
);

export default AdminSettings;
